import { Location } from "@angular/common";
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ColDef } from "@ag-grid-community/core";
import { Account } from "@/model/entities/entity-model";
import { RedZoneJoDetail } from "app/model/entities/-chart-classes";

import { DateFns } from "app/shared/date-fns";
import { DbQueryService } from "app/shared/db-query.service";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import _ from "lodash";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";

/** One aging bucket. maxDays is inclusive; null means no upper limit. */
interface BucketDef {
  label: string;
  maxDays: number | null;
}

interface BucketRow extends BucketDef {
  color: string;
  openCount: number;
  openPct: number;
  openValueAmt: number;
}

/** Days past the ORIGINAL promised ship date. Zero or less is not yet due. */
const BUCKETS: BucketDef[] = [
  { label: "Not yet due", maxDays: 0 },
  { label: "1-10 days late", maxDays: 10 },
  { label: "11-30 days late", maxDays: 30 },
  { label: "31-90 days late", maxDays: 90 },
  { label: "91+ days late", maxDays: null },
];

/** Neutral slate for "not yet due", then an ordered warm ramp for lateness. Concrete hex, not
 *  CSS variables, because AG Charts paints on a canvas. Every adjacent pair passes the dataviz
 *  validator's CVD (>= 8) and normal-vision (>= 15) separation in its own mode; the slices also
 *  carry a 2px gap and the list beside the donut names each one, so color is never alone. */
const LIGHT_COLORS = ["#64748b", "#fde047", "#fb923c", "#ef4444", "#991b1b"];
const DARK_COLORS = ["#94a3b8", "#fde047", "#fb923c", "#ef4444", "#991b1b"];

@Component({
  selector: "app-red-zone",
  templateUrl: "./red-zone.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class RedZoneComponent extends DomainBaseComponent implements OnInit, OnChanges {
  /** Null = every account. */
  @Input() account: Account | null = null;
  /** Accounts set to Exclude from Dashboard Analytics that this view puts back in. */
  @Input() includedAccountIds: number[] = [];

  buckets: BucketRow[] = [];
  totalOpenCount = 0;
  totalValueAmt = 0;
  latePct = 0;
  options: any;

  private detailRows: RedZoneJoDetail[] = [];
  private loadToken = 0;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected location: Location,
    protected dbQueryService: DbQueryService
  ) {
    super(domainService);
  }

  async ngOnInit() {
    await this.load();
  }

  async ngOnChanges(changes: SimpleChanges) {
    const changed = changes["account"] ?? changes["includedAccountIds"];
    if (changed && !changed.firstChange) {
      await this.load();
    }
  }

  /** A newer load (the account filter changed again) wins over a slower older one. */
  private async load() {
    const token = ++this.loadToken;
    this.isPageReady = false;
    const data = (await this.dbQueryService.getRedZoneJoDetails(this.account?.id ?? 0, this.includedAccountIds)) ?? [];
    if (token !== this.loadToken) return;
    DateFns.fixupDatesInProjection(data, ["joDate", "origExpectedShipDate", "detailExpectedShipDate"]);

    this.detailRows = _.orderBy(data, ["daysPastDue", "accountName", "joHeaderId"], ["desc", "asc", "asc"]);
    this.totalOpenCount = _.sumBy(data, (d) => d.openQty);
    this.totalValueAmt = _.sumBy(data, (d) => d.openValueAmt);
    this.buckets = this.buildBuckets(data);
    const lateCount = _.sumBy(this.buckets.slice(1), (b) => b.openCount);
    this.latePct = this.totalOpenCount === 0 ? 0 : (lateCount / this.totalOpenCount) * 100;
    this.options = this.totalOpenCount === 0 ? null : this.buildChartOptions();
    this.isPageReady = true;
  }

  /** Slices run clockwise from 12 o'clock in bucket order, so lateness reads as it deepens.
   *  No labels on the ring: the list beside it carries every number. */
  private buildChartOptions() {
    const css = getComputedStyle(document.documentElement);
    const ink = css.getPropertyValue("--p-text-color").trim() || "#1f2933";
    const muted = css.getPropertyValue("--p-text-muted-color").trim() || "#64748b";
    return {
      background: { visible: false },
      padding: { top: 4, right: 4, bottom: 4, left: 4 },
      legend: { enabled: false },
      data: this.buckets,
      series: [
        {
          type: "donut",
          angleKey: "openCount",
          legendItemKey: "label",
          fills: this.buckets.map((b) => b.color),
          strokeWidth: 0,
          sectorSpacing: 2,
          cornerRadius: 4,
          innerRadiusRatio: 0.62,
          innerLabels: [
            { text: `${_.round(this.latePct, 1)}%`, fontSize: 22, fontWeight: "bold", color: ink },
            { text: "late", fontSize: 12, color: muted },
          ],
          tooltip: { renderer: this.renderer.bind(this) },
        },
      ],
    };
  }

  renderer(params: { datum: BucketRow }) {
    const b = params.datum;
    return {
      title: b.label,
      content: `${b.openCount.toLocaleString()} open (${_.round(b.openPct, 1)}%)<br/>` +
        `${b.openValueAmt.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}`,
    };
  }

  private buildBuckets(data: RedZoneJoDetail[]): BucketRow[] {
    const colors = document.documentElement.classList.contains("app-dark") ? DARK_COLORS : LIGHT_COLORS;
    const byBucket = _.groupBy(data, (d) => this.bucketIndex(d.daysPastDue));
    return BUCKETS.map((b, i) => {
      const rows = byBucket[i] ?? [];
      const openCount = _.sumBy(rows, (r) => r.openQty);
      return {
        ...b,
        color: colors[i],
        openCount,
        openPct: this.totalOpenCount === 0 ? 0 : (openCount / this.totalOpenCount) * 100,
        openValueAmt: _.sumBy(rows, (r) => r.openValueAmt),
      };
    });
  }

  private bucketIndex(daysPastDue: number) {
    const i = BUCKETS.findIndex((b) => b.maxDays !== null && daysPastDue <= b.maxDays);
    return i === -1 ? BUCKETS.length - 1 : i;
  }

  get scopeLabel() {
    return this.account ? this.account.accountName : "All accounts";
  }

  getTitle() {
    return "Red Zone";
  }

  // ViewableWidget interface methods
  async exportToExcel(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Red Zone");
    const exportDate = new Date();

    worksheet.addRow([`${this.getTitle()} - ${this.scopeLabel}`]);
    worksheet.getRow(1).font = { bold: true, size: 14 };
    worksheet.mergeCells("A1:J1");

    worksheet.addRow([`Exported: ${exportDate.toLocaleDateString()} ${exportDate.toLocaleTimeString()}`]);
    worksheet.getRow(2).font = { italic: true, size: 10 };
    worksheet.mergeCells("A2:J2");

    worksheet.addRow([]);

    worksheet.addRow(["Account", "Job Order", "Order Date", "SKU", "Product Type Name", "Features",
      "Original Expected Ship Date", "Detail Expected Ship Date", "Open Count", "Open Value"]);
    const headerRow = worksheet.getRow(4);
    headerRow.font = { bold: true };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };

    [32, 12, 14, 18, 36, 40, 26, 24, 12, 14].forEach((w, i) => (worksheet.getColumn(i + 1).width = w));

    this.detailRows.forEach((r) => {
      worksheet.addRow([
        r.accountName,
        r.joHeaderId,
        r.joDate,
        r.sku,
        r.productTypeName,
        r.features,
        r.origExpectedShipDate,
        r.detailExpectedShipDate,
        r.openQty,
        r.openValueAmt,
      ]);
    });
    worksheet.getColumn(3).numFmt = "mm/dd/yyyy";
    worksheet.getColumn(7).numFmt = "mm/dd/yyyy";
    worksheet.getColumn(8).numFmt = "mm/dd/yyyy";
    worksheet.getColumn(10).numFmt = '"$"#,##0.00';

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `${this.getExportFileName()}_${exportDate.toISOString().split("T")[0]}.xlsx`);
  }

  getGridData(): RedZoneJoDetail[] {
    return this.detailRows;
  }

  getColumnDefs(): ColDef[] {
    return [
      { field: "accountName", headerName: "Account", flex: 2 },
      { field: "joHeaderId", headerName: "Job Order", flex: 1, type: "leftAligned" },
      { field: "joDate", headerName: "Order Date", flex: 1 },
      { field: "sku", headerName: "SKU", flex: 1 },
      { field: "productTypeName", headerName: "Product Type Name", flex: 2 },
      { field: "features", headerName: "Features", flex: 2 },
      { field: "origExpectedShipDate", headerName: "Original Expected Ship Date", flex: 1 },
      { field: "detailExpectedShipDate", headerName: "Detail Expected Ship Date", flex: 1 },
      { field: "openQty", headerName: "Open Count", flex: 1, type: "leftAligned" },
      { field: "openValueAmt", headerName: "Open Value", flex: 1 },
    ];
  }

  getExportFileName(): string {
    return "Red_Zone";
  }

  getDialogTitle(): string {
    return `${this.getTitle()} - ${this.scopeLabel}`;
  }
}
